import { BaseForm, DatePicker, Form, Input, Radio } from '@vivaldis/antd-ui'
import { useTranslation } from 'react-i18next'
import { Grid } from '../../../../../Grid'
import { useWPSItemIndex } from '../../../../hooks/useWPSItemIndex'
import { useAiSuggestion } from '../../../../providers/AiSuggestionProvider'
import { useForm } from '../../../../providers/FormProvider'
import { WorkPostSheetItemTypesEnum } from '../../../../types'
import { LabelWithAISuggestion } from '../../../LabelWithAISuggestion'
import { WPSItemCheckbox } from '../../../WPSItemCheckbox'

export const Step4 = () => {
  const [t] = useTranslation('web_ui')

  const { values } = useForm()
  const { isMaternityProtectionMeasuresRequiredAiSuggested } = useAiSuggestion()

  return (
    <>
      <Form.Item
        name="maternityProtectionMeasuresRequired"
        required
        label={
          <LabelWithAISuggestion
            label={t('work_post_sheet_form.group_4.label')}
            aiSuggested={isMaternityProtectionMeasuresRequiredAiSuggested}
          />
        }
        tooltip={t('work_post_sheet_form.group_4.tooltip')}
      >
        <Radio.Group name="maternityProtectionMeasuresRequired">
          <Radio name="maternityProtectionMeasuresRequired" value={true}>
            {t('work_post_sheet_form.group_4.options.yes')}
          </Radio>
          <Radio name="maternityProtectionMeasuresRequired" value={false}>
            {t('work_post_sheet_form.group_4.options.no')}
          </Radio>
        </Radio.Group>
      </Form.Item>
      {values.maternityProtectionMeasuresRequired ? (
        <Grid columns={1} gap={0} columnGap={16} marginTop={6}>
          <WPSItemTextField
            name={WorkPostSheetItemTypesEnum.PREGNANCY_ADJUSTMENTS}
            allowClear
          />
          <WPSItemDateField
            name={WorkPostSheetItemTypesEnum.PREGNANCY_ADJUSTMENTS_DATE}
            allowClear
          />
          <WPSItemTextField
            name={WorkPostSheetItemTypesEnum.BREASTFEEDING_ADJUSTMENTS}
          />
          <WPSItemDateField
            name={WorkPostSheetItemTypesEnum.BREASTFEEDING_ADJUSTEMENTS_DATE}
          />
        </Grid>
      ) : null}

      <BaseForm.Item
        label={t('work_post_sheet_form.group_5.label')}
        tooltip={t('work_post_sheet_form.group_5.tooltip')}
      >
        <Grid columns={1} gap={0} columnGap={16} marginTop={6}>
          <WPSItemDateField
            name={WorkPostSheetItemTypesEnum.ADVICE_DATE}
            allowClear
          />
          <WPSItemCheckbox
            name={WorkPostSheetItemTypesEnum.ADVICE_BY_COMITE_PWB}
            withComment={false}
          />
          <WPSItemCheckbox
            name={WorkPostSheetItemTypesEnum.ADVICE_BY_SYNDICATE_REP}
            withComment={false}
          />
          <WPSItemCheckbox
            name={
              WorkPostSheetItemTypesEnum.ADVICE_BY_DIRECT_EMPLOYEE_PARTICIPATION
            }
            withComment={false}
          />
          <div>
            <WPSItemDateField
              name={WorkPostSheetItemTypesEnum.ADVICE_DATE_LABOR_DOCTOR}
              allowClear
            />
            <WPSItemDateField
              name={WorkPostSheetItemTypesEnum.ADVICE_DATE_INTERNAL_SERVICE}
            />
          </div>
        </Grid>
      </BaseForm.Item>
    </>
  )
}

interface WPSItemFieldProps {
  name: WorkPostSheetItemTypesEnum
  allowClear?: boolean
}

// item that is stored in `comment` without a checkbox (see isTextWPSItem)
const WPSItemTextField = ({ name, allowClear }: WPSItemFieldProps) => {
  const [t] = useTranslation('web_ui')
  const { isItemAiSuggested } = useAiSuggestion()
  const getItemIndex = useWPSItemIndex()

  const fieldName = `items[${getItemIndex(name)}].comment`

  return (
    <Form.Item
      name={fieldName}
      label={
        <LabelWithAISuggestion
          label={t(`work_post_sheet_form.${name.toLocaleLowerCase()}.label`)}
          aiSuggested={isItemAiSuggested(name)}
        />
      }
    >
      <Input name={fieldName} allowClear={allowClear} />
    </Form.Item>
  )
}

// item that is stored in `comment` as a date without a checkbox (see isDateWPSItem)
const WPSItemDateField = ({ name, allowClear }: WPSItemFieldProps) => {
  const [t] = useTranslation('web_ui')
  const { isItemAiSuggested } = useAiSuggestion()
  const getItemIndex = useWPSItemIndex()

  const fieldName = `items[${getItemIndex(name)}].comment`

  return (
    <Form.Item
      name={fieldName}
      label={
        <LabelWithAISuggestion
          label={t(`work_post_sheet_form.${name.toLocaleLowerCase()}.label`)}
          aiSuggested={isItemAiSuggested(name)}
        />
      }
    >
      <DatePicker name={fieldName} allowClear={allowClear} />
    </Form.Item>
  )
}
